"use client";

import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import {
  AlertTriangle,
  ArrowRight,
  BellRing,
  Calculator,
  Code2,
  Crown,
  FileText,
  Gauge,
  Gem,
  Gift,
  Headset,
  Infinity as InfinityIcon,
  Layers,
  Leaf,
  Radar,
  ScanSearch,
  Search,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  X,
  Zap,
} from "lucide-react";
import { Button } from "@carpybara/ui/shadcn/button";
import { cn } from "@carpybara/ui/shadcn/utils";
import { Textarea } from "@carpybara/ui/shadcn/textarea";
import { AppModal } from "../../components/app-modal";
import { SelectPopover } from "../../components/filters/select-popover";
import {
  PlanFeatureList,
  PlanScene,
} from "../../components/pricing-plan-parts";
import { useShell } from "../../components/page-shell";
import {
  renderSubscriptionPlanNames,
  SubscriptionPlanName,
} from "../../components/subscription-plan-name";
import { isProOrBetter } from "../../lib/tier";
import { currencyDisplayFor } from "../../lib/locale";
import {
  subscriptionPlanTone,
  type PaidSubscriptionTier,
} from "../../lib/subscription-plan-tone";
import { pricingPlanAnchorId } from "../../lib/subscription-plan-link";
import { Spinner } from "@carpybara/ui";
import {
  buildLoginPath,
  buildRegisterPath,
  sanitizeLoginPath,
  sanitizeRelativePath,
} from "../../lib/url-safety";
import { track } from "@carpybara/analytics/client";
import {
  trackSubscriptionPurchase,
  type SubscriptionPurchase,
} from "../../lib/google-analytics";
import {
  getGrowthSessionContext,
  recordClientGrowthFunnelStep,
} from "../../lib/growth-funnel-client";
import { coarsePricingSource } from "../../lib/growth-funnel-shared";

const ACTIVE_ADDON_STATUSES = new Set(["active", "trialing", "past_due"]);

type AddonSub = {
  subscriptionId: string;
  status: string;
  pack: string | null;
  cancelAtPeriodEnd: boolean;
};

function withReturnTo(settingsUrl: string, returnTo: string): string {
  if (typeof window === "undefined") return settingsUrl;
  const url = new URL(settingsUrl, window.location.origin);
  url.searchParams.set("returnTo", returnTo);
  return `${url.pathname}${url.search}${url.hash}`;
}

export default function PricingPage() {
  return (
    <Suspense fallback={null}>
      <PricingPageInner />
    </Suspense>
  );
}

function formatCurrency(
  amount: number,
  currency: string,
  locale: string,
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency.toUpperCase(),
      currencyDisplay: currencyDisplayFor(currency.toUpperCase()),
    }).format(amount);
  } catch {
    return `${currency.toUpperCase()} ${amount.toFixed(2)}`;
  }
}

function PricingPageInner() {
  const { data: session, status, update } = useSession();
  const { t, locale } = useShell();
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const callbackUrl = sanitizeRelativePath(params.get("callbackUrl"));
  const pricingSource =
    params.get("source") === "guest_deal_sample"
      ? "guest_deal_sample"
      : null;
  const repairEstimateUsd = (() => {
    const value = Number(params.get("repairEstimateUsd"));
    return Number.isFinite(value) && value >= 100 && value <= 250_000
      ? Math.round(value)
      : null;
  })();
  const repairValueProof =
    repairEstimateUsd === null
      ? null
      : {
          estimateUsd: repairEstimateUsd,
          proMonths: Math.max(
            1,
            Math.min(999, Math.round(repairEstimateUsd / 19)),
          ),
        };
  const checkoutResult = params.get("checkout"); // "success" | "cancel" | null
  const requestedPlan = params.get("plan") === "ultra" ? "ultra" : "pro";
  // `?plan=` is also what upgrade prose elsewhere deep-links with (see
  // `pricingPlanHref`). Unlike `requestedPlan` this stays null when the param
  // is absent, so the plain /pricing visit highlights nothing.
  const rawPlanParam = params.get("plan");
  const highlightedPlan: PaidSubscriptionTier | null =
    rawPlanParam === "ultra" ? "ultra" : rawPlanParam === "pro" ? "pro" : null;
  const resumeCheckout = params.get("resumeCheckout") === "1";
  const pricingReturnUrl = useMemo(() => {
    const basePath = pathname || "/pricing";
    const nextParams = new URLSearchParams();
    if (callbackUrl) nextParams.set("callbackUrl", callbackUrl);
    if (pricingSource) nextParams.set("source", pricingSource);
    if (repairEstimateUsd) {
      nextParams.set("repairEstimateUsd", String(repairEstimateUsd));
    }
    if (resumeCheckout) {
      nextParams.set("plan", requestedPlan);
      nextParams.set("resumeCheckout", "1");
    }
    const query = nextParams.toString();
    return query ? `${basePath}?${query}` : basePath;
  }, [
    callbackUrl,
    pathname,
    pricingSource,
    repairEstimateUsd,
    requestedPlan,
    resumeCheckout,
  ]);
  const loginUrl = useMemo(
    () => buildLoginPath(pricingReturnUrl),
    [pricingReturnUrl],
  );
  const [loading, setLoading] = useState<
    "upgrade" | "upgrade-ultra" | "manage" | "cancel" | null
  >(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelDetails, setCancelDetails] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [paymentDeclined, setPaymentDeclined] = useState(false);

  const [activating, setActivating] = useState(false);

  const [ultraPreview, setUltraPreview] = useState<{
    amountDueNow: number;
    currency: string;
    nextRenewalDate: string | null;
  } | null>(null);

  const [activeAddons, setActiveAddons] = useState<AddonSub[]>([]);
  const [showConfirmUpgradeModal, setShowConfirmUpgradeModal] = useState(false);

  const updateRef = useRef(update);
  updateRef.current = update;
  const autoResumeRef = useRef(false);

  // Force a session refresh on mount so the pricing page always shows the
  // latest subscription state (e.g. after returning from Stripe Billing Portal).
  const refreshedRef = useRef(false);
  useEffect(() => {
    if (refreshedRef.current) return;
    refreshedRef.current = true;
    update({});
  }, [update]);

  // Bring the deep-linked card into view once. The cards render after
  // hydration, so the browser's own hash scroll has usually already run and
  // found nothing. Scroll once per requested plan, never on a plain visit.
  const scrolledToPlanRef = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightedPlan) return;
    if (scrolledToPlanRef.current === highlightedPlan) return;
    scrolledToPlanRef.current = highlightedPlan;
    const node = document.getElementById(pricingPlanAnchorId(highlightedPlan));
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightedPlan]);

  const tier = session?.user?.tier ?? "free";
  const pricingViewTrackedRef = useRef(false);
  useEffect(() => {
    if (status === "loading" || pricingViewTrackedRef.current) return;
    pricingViewTrackedRef.current = true;
    const resolvedTier =
      tier === "ultra" ? "ultra" : tier === "pro" ? "pro" : "free";
    track("pricing_viewed", {
      tier: resolvedTier,
      source: callbackUrl ?? undefined,
    });
    recordClientGrowthFunnelStep("pricing_viewed", {
      tier: resolvedTier,
      source: coarsePricingSource(callbackUrl),
    });
  }, [callbackUrl, status, tier]);
  const bonusEndsAtMs = session?.user?.bonusEndsAt
    ? new Date(session.user.bonusEndsAt).getTime()
    : Number.NaN;
  const activeBonusTier =
    (session?.user?.bonusTier === "pro" ||
      session?.user?.bonusTier === "ultra") &&
    Number.isFinite(bonusEndsAtMs) &&
    bonusEndsAtMs > Date.now()
      ? session.user.bonusTier
      : null;
  const currentPlanName =
    tier === "ultra"
      ? t("pricing.ultra.name")
      : tier === "pro"
        ? t("pricing.pro.name")
        : t("pricing.free.name");
  const currentPaidTier: PaidSubscriptionTier | null =
    tier === "ultra" ? "ultra" : tier === "pro" ? "pro" : null;
  const activeBonusTone = activeBonusTier
    ? subscriptionPlanTone(activeBonusTier)
    : null;
  const bonusEndsAtLabel = activeBonusTier
    ? new Intl.DateTimeFormat(locale, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(bonusEndsAtMs))
    : "";
  // Server is the source of truth for intro eligibility — per plan. The
  // checkout route mirrors this check against the user doc, so the UI label
  // and the actual Stripe session always agree. Signed-out visitors are
  // treated as eligible for both so the marketing copy renders.
  const introEligibleFor = session?.user?.introOfferEligibleFor ?? [
    "pro",
    "ultra",
  ];
  const proIntroEligible = introEligibleFor.includes("pro");
  const ultraIntroEligible = introEligibleFor.includes("ultra");
  // "Intro week active" detection. The intro flow uses
  // billing_cycle_anchor + a 100%-off coupon instead of Stripe's native
  // trial (which forces a "7 days free" badge in Checkout), so the sub
  // is `active` from day 0 and `subscriptionStatus === "trialing"` no
  // longer fires. The server sets `trialEndsAt` whenever the sub is in
  // its first (intro) cycle and clears it once the cycle expands to the
  // regular ~30-day window. So "intro active" === "trialEndsAt is set
  // and in the future". Also keeps working for legacy trialing subs.
  const trialEndsAtMs = session?.user?.trialEndsAt
    ? new Date(session.user.trialEndsAt).getTime()
    : null;
  const introActive = trialEndsAtMs !== null && trialEndsAtMs > Date.now();
  const isProTrial = tier === "pro" && introActive;
  const isUltraTrial = tier === "ultra" && introActive;

  // Show what the user would actually pay today for an Ultra upgrade.
  // Stripe credits unused Pro time and bills only the difference up-front;
  // this hits invoices.createPreview so the card displays the real number
  // instead of the static $199 sticker price.
  useEffect(() => {
    // Skip the proration preview when the Pro sub is still trialing — no
    // recurring charge has billed yet so there's nothing to credit, and
    // the Ultra checkout goes through the new-sub path (Pro is cancelled
    // first), not the in-place upgrade the preview models.
    if (tier !== "pro" || isProTrial) {
      setUltraPreview(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/billing/preview-upgrade", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ plan: "ultra" }),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data?.available) return;
        setUltraPreview({
          amountDueNow: data.amountDueNow,
          currency: data.currency,
          nextRenewalDate: data.nextRenewalDate ?? null,
        });
      } catch {
        // swallow — card just falls back to the static price
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tier, isProTrial]);

  // Detect active alert add-on subs so we can warn before upgrading to Ultra.
  // Ultra includes unlimited alerts, so the add-on becomes redundant and we
  // want explicit consent before cancelling it. Also fires on free tier — a
  // user could have a leftover addon after their Pro plan ended.
  useEffect(() => {
    if (status !== "authenticated" || tier === "ultra") {
      setActiveAddons([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/user/car-notifications-quota");
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const subs: AddonSub[] = Array.isArray(data?.addonSubs)
          ? data.addonSubs
          : [];
        const live = subs.filter(
          (s) => ACTIVE_ADDON_STATUSES.has(s.status) && !s.cancelAtPeriodEnd,
        );
        setActiveAddons(live);
      } catch {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, tier]);

  // When Stripe redirects us back with ?checkout=success, first try to
  // activate the plan directly by handing the session id to the server
  // (/api/billing/confirm retrieves it from Stripe and flips the tier).
  // That races the subscription webhook so the UI doesn't have to wait on
  // webhook delivery. If confirm reports the subscription isn't ready yet,
  // fall back to polling /api/user/checks until the webhook catches up.
  // The query string is stripped once activation completes so a reload
  // doesn't re-trigger.
  useEffect(() => {
    if (checkoutResult !== "success") return;
    setActivating(true);
    let cancelled = false;
    const MAX_ATTEMPTS = 15;
    const INTERVAL = 2000;
    const sessionId = params.get("session_id");

    const confirmCheckout = async (): Promise<{
      tier?: string;
      purchase?: SubscriptionPurchase | null;
    } | null> => {
      if (!sessionId) return null;
      try {
        const res = await fetch("/api/billing/confirm", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
        });
        if (!res.ok) return null;
        return await res.json();
      } catch {
        return null;
      }
    };

    const finish = async () => {
      try {
        await updateRef.current({});
      } catch {
        console.error("Failed to refresh session");
      }
      await new Promise((r) => setTimeout(r, 500));
      if (cancelled) return true;
      if (callbackUrl) {
        router.push(callbackUrl);
      } else {
        router.replace("/pricing");
      }
      return true;
    };

    (async () => {
      if (sessionId) {
        const data = await confirmCheckout();
        if (!cancelled && data?.tier && data.tier !== "free") {
          if (data.purchase) trackSubscriptionPurchase(data.purchase);
          await finish();
          return;
        }
      }

      for (let i = 0; i < MAX_ATTEMPTS; i++) {
        if (cancelled) return;
        await new Promise((r) => setTimeout(r, INTERVAL));
        if (cancelled) return;
        try {
          const res = await fetch("/api/user/checks");
          if (res.ok) {
            const data = await res.json();
            if (data.tier && data.tier !== "free") {
              // The webhook may have won the race. Re-read the verified
              // Checkout Session once so the standard purchase event still
              // carries its real amount, currency, plan and transaction id.
              const confirmation = await confirmCheckout();
              if (confirmation?.purchase) {
                trackSubscriptionPurchase(confirmation.purchase);
              }
              await finish();
              return;
            }
          }
        } catch {
          // ignore – will retry
        }
      }
      if (!cancelled) setActivating(false);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutResult, callbackUrl, router]);

  // Surface a friendly message if the user returned from a cancelled checkout.
  useEffect(() => {
    if (checkoutResult === "cancel") {
      setError(t("pricing.errors.checkoutFailed"));
      // Clear the query string so it doesn't persist on reload.
      router.replace("/pricing");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutResult]);

  // Treat Ultra as "already pro or better" so Ultra users see the same
  // post-upgrade UI (no "Upgrade" CTA, current plan shown as active).
  const isPro = isProOrBetter(tier);
  const subscriptionStatus = session?.user?.subscriptionStatus ?? null;
  const endsAtIso = session?.user?.subscriptionEndsAt ?? null;
  // Stripe uses "canceled" (US spelling). A subscription is also effectively
  // cancelled when it's still "active" but has a scheduled end date.
  const isCancelled =
    subscriptionStatus === "canceled" ||
    subscriptionStatus === "cancelled" ||
    (endsAtIso !== null && new Date(endsAtIso).getTime() > Date.now());
  const dateLocale = locale === "uk" ? "uk-UA" : "en-US";
  const endsAtLabel = endsAtIso
    ? new Date(endsAtIso).toLocaleDateString(dateLocale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
    : null;
  const trialEndsAtIso = session?.user?.trialEndsAt ?? null;
  const trialEndsAtLabel = trialEndsAtIso
    ? new Date(trialEndsAtIso).toLocaleDateString(dateLocale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
    : null;

  const showProrationLine = tier === "pro" && ultraPreview != null;
  const showAddonLine = activeAddons.length > 0;

  function redirectToLogin() {
    setError(null);
    setPaymentDeclined(false);
    setLoading(null);
    router.push(loginUrl);
  }

  function handleUpgradeClick(plan: "pro" | "ultra" = "pro") {
    const resolvedTier =
      tier === "ultra" ? "ultra" : tier === "pro" ? "pro" : "free";
    track("upgrade_clicked", {
      plan,
      tier: resolvedTier,
      surface: "pricing",
    });
    recordClientGrowthFunnelStep("upgrade_clicked", {
      plan,
      tier: resolvedTier,
      surface: "pricing",
    });
    if (status === "unauthenticated") {
      const nextParams = new URLSearchParams();
      if (callbackUrl) nextParams.set("callbackUrl", callbackUrl);
      if (pricingSource) nextParams.set("source", pricingSource);
      if (repairEstimateUsd) {
        nextParams.set("repairEstimateUsd", String(repairEstimateUsd));
      }
      nextParams.set("plan", plan);
      nextParams.set("resumeCheckout", "1");
      redirectToRegisterWithPath(`/pricing?${nextParams.toString()}`);
      return;
    }
    if (status === "loading") return;
    if (plan === "ultra" && (showProrationLine || showAddonLine)) {
      setShowConfirmUpgradeModal(true);
      return;
    }
    void handleUpgrade(plan);
  }

  function redirectToRegisterWithPath(returnPath: string) {
    setError(null);
    setPaymentDeclined(false);
    setLoading(null);
    router.push(buildRegisterPath(returnPath));
  }

  function handleConfirmUpgrade() {
    setShowConfirmUpgradeModal(false);
    void handleUpgrade("ultra");
  }

  async function handleUpgrade(plan: "pro" | "ultra" = "pro") {
    if (status !== "authenticated") {
      redirectToLogin();
      return;
    }

    setError(null);
    setPaymentDeclined(false);
    setLoading(plan === "ultra" ? "upgrade-ultra" : "upgrade");
    try {
      const growthContext = getGrowthSessionContext();
      const res = await fetch("/api/billing/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan,
          callbackUrl,
          returnTo: pricingReturnUrl,
          ...growthContext,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        const nextLoginUrl = sanitizeLoginPath(data?.loginUrl) ?? loginUrl;
        setLoading(null);
        router.push(nextLoginUrl);
        return;
      }
      if (res.status === 402 && data?.error === "payment_failed") {
        setPaymentDeclined(true);
        setError(t("pricing.errors.paymentDeclined"));
        setLoading(null);
        return;
      }
      if (data?.error === "missing_email") {
        const settingsUrl =
          sanitizeRelativePath(data?.settingsUrl) ??
          "/settings?focus=email#profile";
        setLoading(null);
        router.push(withReturnTo(settingsUrl, pricingReturnUrl));
        return;
      }
      if (!res.ok || !data.url) {
        throw new Error(
          data.error === "intro_offer_unavailable" && data.message
            ? data.message
            : (data.error ?? t("pricing.errors.checkoutFailed")),
        );
      }
      // Redirect to Stripe Checkout. On success/cancel Stripe will send the
      // user back to /pricing?checkout=success|cancel.
      window.location.href = data.url;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("pricing.errors.unknown")
      );
      setLoading(null);
    }
  }

  useEffect(() => {
    if (
      !resumeCheckout ||
      checkoutResult ||
      status !== "authenticated" ||
      tier !== "free" ||
      autoResumeRef.current
    ) {
      return;
    }
    autoResumeRef.current = true;
    void handleUpgrade(requestedPlan);
    // handleUpgrade is intentionally triggered once from URL intent; including
    // its render-local identity would re-run the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutResult, requestedPlan, resumeCheckout, status, tier]);

  async function handleManage() {
    if (status === "unauthenticated") {
      redirectToLogin();
      return;
    }
    if (status === "loading") return;

    setError(null);
    setLoading("manage");
    try {
      const res = await fetch("/api/billing/portal", { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        const nextLoginUrl = sanitizeLoginPath(data?.loginUrl) ?? loginUrl;
        setLoading(null);
        router.push(nextLoginUrl);
        return;
      }
      if (!res.ok || !data.url) {
        throw new Error(data.error ?? t("pricing.errors.portalFailed"));
      }
      window.location.href = data.url;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("pricing.errors.unknown")
      );
      setLoading(null);
    }
  }

  // Keep cancellation in-app for every plan so we can collect a structured
  // reason and show retention options consistently.
  function handleCancel() {
    if (status === "unauthenticated") {
      redirectToLogin();
      return;
    }
    if (status === "loading") return;
    setError(null);
    setCancelReason("");
    setCancelDetails("");
    setShowCancelModal(true);
  }

  async function confirmCancel() {
    setError(null);
    setLoading("cancel");
    try {
      const res = await fetch("/api/billing/cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "cancel",
          reason: cancelReason,
          details: cancelDetails,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        const nextLoginUrl = sanitizeLoginPath(data?.loginUrl) ?? loginUrl;
        setLoading(null);
        router.push(nextLoginUrl);
        return;
      }
      if (!res.ok || !data.ok) {
        throw new Error(data.error ?? t("pricing.errors.unknown"));
      }
      // Refresh the session so the page picks up the scheduled end date the
      // endpoint just wrote (isCancelled flips true, the cancel button hides).
      await update({});
      setShowCancelModal(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("pricing.errors.unknown")
      );
    } finally {
      setLoading(null);
    }
  }

  return (
    <div className="w-full space-y-10">
      <header className="text-center space-y-3">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-(--text-primary)">
          {tier === "free" ? (
            <>
              {t("pricing.heading")}{" "}
              <span>{renderSubscriptionPlanNames(t("pricing.headingBrand"))}</span>
            </>
          ) : (
            <>
              {t("pricing.currentHeading")}{" "}
              <span>
                carpybara{" "}
                {currentPaidTier ? (
                  <SubscriptionPlanName tier={currentPaidTier}>
                    {currentPlanName}
                  </SubscriptionPlanName>
                ) : (
                  currentPlanName
                )}
              </span>
            </>
          )}
        </h1>
        <p className="text-sm text-(--text-muted)">
          {tier === "free"
            ? t("pricing.subheading")
            : t("pricing.currentSubheading")}
        </p>
      </header>

      {activeBonusTier ? (
        <div
          className={cn(
            "overflow-hidden rounded-2xl border px-5 py-4",
            activeBonusTone?.panelClassName,
          )}
        >
          <div className="flex items-start gap-3">
            <span
              className={cn(
                "inline-flex size-10 shrink-0 items-center justify-center rounded-xl",
                activeBonusTone?.badgeClassName,
              )}
            >
              <Gift className="size-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-(--text-primary)">
                {renderSubscriptionPlanNames(
                  t("pricing.bonusTitle", { plan: currentPlanName }),
                )}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-(--text-muted)">
                {t("pricing.bonusBody", { date: bonusEndsAtLabel })}
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {tier === "free" && repairValueProof ? (
        <div className="flex flex-col gap-3 rounded-2xl border border-(--status-success-border) bg-(--status-success-bg) px-5 py-4 sm:flex-row sm:items-center">
          <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-(--status-success-bg) text-(--status-success)">
            <ShieldCheck className="size-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-(--text-primary)">
              {t("pricing.valueProof.title", {
                amount: formatCurrency(
                  repairValueProof.estimateUsd,
                  "USD",
                  locale,
                ),
              })}
            </p>
            <p className="mt-1 text-xs leading-relaxed text-(--text-muted)">
              {renderSubscriptionPlanNames(
                t("pricing.valueProof.body", {
                  months: repairValueProof.proMonths,
                }),
              )}
            </p>
          </div>
        </div>
      ) : null}

      {activating && !isPro && (
        <div className="rounded-xl border border-(--status-success-border) bg-(--status-success-bg) px-4 py-3 text-sm text-(--status-success-text) flex items-center gap-3">
          <Spinner size="sm" />
          {t("pricing.activating")}
        </div>
      )}

      {tier === "free" && proIntroEligible && ultraIntroEligible && (
        <div className="overflow-hidden rounded-2xl border border-(--accent)/40 bg-(--accent)/5 px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-(--accent)/15 text-(--accent)">
              <Sparkles className="h-4 w-4" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-(--text-primary)">
                {t("pricing.introBanner.title")}
              </p>
              <p className="mt-1 text-xs text-(--text-muted) leading-relaxed">
                {t("pricing.introBanner.body")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* three cards side by side only where each gets room for its scene (the page area, not the window, decides) */}
      <div className="@container">
        <div className="plan-stage grid grid-cols-1 gap-4 @4xl:grid-cols-3">
          {/* Free tier */}
          <div className="plan-shell mx-auto flex w-full max-w-md flex-col @4xl:max-w-none" data-plan="free">
            <div className="plan-card flex flex-1 flex-col overflow-hidden rounded-2xl border border-(--border) bg-(--bg-card)">
              <PlanScene plan="free" icon={Leaf} caption={t("pricing.free.scene")} />
              <div className="flex flex-1 flex-col gap-4 p-6">
                <div>
                  <h2 className="text-xl font-bold text-(--text-primary)">
                    {t("pricing.free.name")}
                  </h2>
                  <p className="text-sm text-(--text-muted)">
                    {t("pricing.free.tagline")}
                  </p>
                </div>
                <p className="text-3xl font-bold text-(--text-primary)">
                  $0
                  <span className="text-sm text-(--text-muted) font-medium">
                    {t("pricing.perMonth")}
                  </span>
                </p>
                <PlanFeatureList
                  plan="free"
                  features={[
                    { icon: Search, text: t("pricing.free.feature1") },
                    { icon: ScanSearch, text: t("pricing.free.feature2") },
                    { icon: BellRing, text: t("pricing.free.feature3") },
                    { icon: Calculator, text: t("pricing.free.feature4") },
                  ]}
                />
                <div className="mt-auto">
                  {tier === "free" && (
                    <p className="text-xs text-(--accent) font-semibold uppercase tracking-wide">
                      {t("pricing.currentPlan")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Pro tier */}
          <div
            id={pricingPlanAnchorId("pro")}
            data-plan="pro"
            className={cn(
              "plan-shell relative mx-auto flex w-full max-w-md flex-col @4xl:max-w-none",
              "scroll-mt-24",
              highlightedPlan === "pro" &&
                "ring-2 ring-(--accent) ring-offset-2 ring-offset-(--bg-primary)",
            )}
          >
            <span className="plan-rim" aria-hidden />
            <div className="plan-card relative flex flex-1 flex-col overflow-hidden rounded-2xl border-2 border-(--accent)/60 bg-(--bg-card) bg-clip-padding">
              <PlanScene plan="pro" icon={Gem} caption={t("pricing.pro.scene")} />
              <div className="flex flex-1 flex-col gap-4 p-6">
                <div>
                  <h2 className="text-xl font-bold text-(--text-primary)">
                    <SubscriptionPlanName tier="pro">
                      {t("pricing.pro.name")}
                    </SubscriptionPlanName>
                  </h2>
                  <p className="text-sm text-(--text-muted)">
                    {renderSubscriptionPlanNames(t("pricing.pro.tagline"))}
                  </p>
                </div>
                {tier === "free" && proIntroEligible ? (
                  <div>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <p className="text-3xl font-bold text-(--text-primary)">
                        $2.99
                      </p>
                      <span className="inline-flex items-center rounded-full bg-(--accent)/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-(--accent)">
                        {t("pricing.trialBadge")}
                      </span>
                    </div>
                    <p className="text-sm text-(--text-primary) mt-1 font-medium">
                      {t("pricing.thenMonthly", { amount: "$19" })}
                    </p>
                  </div>
                ) : (
                  <p className="text-3xl font-bold text-(--text-primary)">
                    $19
                    <span className="text-sm text-(--text-muted) font-medium">
                      {t("pricing.perMonth")}
                    </span>
                  </p>
                )}
                <PlanFeatureList
                  plan="pro"
                  features={[
                    { icon: ScanSearch, text: t("pricing.pro.feature1") },
                    { icon: TrendingUp, text: t("pricing.pro.feature2") },
                    { icon: Headset, text: t("pricing.pro.feature3") },
                    { icon: Gauge, text: t("pricing.pro.feature4") },
                    { icon: BellRing, text: t("pricing.pro.feature5") },
                    { icon: FileText, text: t("pricing.pro.feature6") },
                  ]}
                />

                <div className="mt-auto">
                  {tier === "pro" ? (
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-xs text-(--accent) font-semibold uppercase tracking-wide">
                          {t("pricing.currentPlan")}
                        </p>
                        {isProTrial && (
                          <span className="inline-flex items-center rounded-full bg-(--accent)/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-(--accent)">
                            {t("pricing.trialActiveBadge")}
                          </span>
                        )}
                      </div>
                      {isProTrial && trialEndsAtLabel && (
                        <p className="text-sm text-(--text-primary) font-medium">
                          {t("pricing.trialEndsOn", { date: trialEndsAtLabel })}
                        </p>
                      )}
                      {isProTrial && (
                        <p className="text-sm text-(--text-primary) leading-relaxed">
                          {t("pricing.trialBillingNote", { amount: "$19" })}
                        </p>
                      )}
                      {isCancelled && endsAtLabel && (
                        <p className="text-xs text-(--text-muted)">
                          {t("pricing.pro.cancelledNotice", { date: endsAtLabel })}
                        </p>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        fullWidth
                        onClick={() => handleManage()}
                        disabled={loading !== null}
                        loading={loading === "manage"}
                      >
                        {loading === "manage"
                          ? t("pricing.pro.manageBtnLoading")
                          : t("pricing.pro.manageBtn")}
                      </Button>
                      {!isCancelled && (
                        <Button
                          variant="ghost"
                          size="sm"
                          fullWidth
                          onClick={() => handleCancel()}
                          disabled={loading !== null}
                          className="text-xs text-(--text-faint) hover:text-(--status-danger) hover:bg-transparent"
                        >
                          {t("pricing.cancelBtn")}
                        </Button>
                      )}
                    </div>
                  ) : tier === "ultra" ? (
                    <p className="text-xs text-purple-400 font-semibold uppercase tracking-wide">
                      {t("pricing.includedInUltra")}
                    </p>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      fullWidth
                      onClick={() => handleUpgradeClick("pro")}
                      disabled={loading !== null || status === "loading"}
                      loading={loading === "upgrade" || status === "loading"}
                      className="h-auto min-h-11 whitespace-normal py-2.5 leading-tight wrap-break-word"
                    >
                      {loading === "upgrade"
                        ? t("pricing.pro.upgradeBtnLoading")
                        : proIntroEligible
                          ? t("pricing.pro.upgradeBtnIntro")
                          : t("pricing.pro.upgradeBtn")}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Ultra tier */}
          <div
            id={pricingPlanAnchorId("ultra")}
            data-plan="ultra"
            className={cn(
              "plan-shell relative mx-auto flex w-full max-w-md flex-col @4xl:max-w-none",
              "scroll-mt-24",
              highlightedPlan === "ultra" &&
                "ring-2 ring-purple-500 ring-offset-2 ring-offset-(--bg-primary)",
            )}
          >
            <span className="plan-rim" aria-hidden />
            <div className="plan-card relative flex flex-1 flex-col overflow-hidden rounded-2xl border-2 border-purple-500/60 bg-(--bg-card) bg-clip-padding">
              <PlanScene plan="ultra" icon={Crown} caption={t("pricing.ultra.scene")} />
              <div className="flex flex-1 flex-col gap-4 p-6">
                <div>
                  <h2 className="text-xl font-bold text-(--text-primary)">
                    <SubscriptionPlanName tier="ultra">
                      {t("pricing.ultra.name")}
                    </SubscriptionPlanName>
                  </h2>
                  <p className="text-sm text-(--text-muted)">
                    {renderSubscriptionPlanNames(t("pricing.ultra.tagline"))}
                  </p>
                </div>
                {(tier === "free" || isProTrial) && ultraIntroEligible ? (
                  <div>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <p className="text-3xl font-bold text-(--text-primary)">
                        $29.99
                      </p>
                      <span className="inline-flex items-center rounded-full bg-purple-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-purple-400">
                        {t("pricing.trialBadge")}
                      </span>
                    </div>
                    <p className="text-sm text-(--text-primary) mt-1 font-medium">
                      {t("pricing.thenMonthly", { amount: "$199" })}
                    </p>
                  </div>
                ) : (
                  <p className="text-3xl font-bold text-(--text-primary)">
                    $199
                    <span className="text-sm text-(--text-muted) font-medium">
                      {t("pricing.perMonth")}
                    </span>
                  </p>
                )}
                <PlanFeatureList
                  plan="ultra"
                  features={[
                    { icon: Layers, text: t("pricing.ultra.feature1") },
                    { icon: Radar, text: t("pricing.ultra.feature2") },
                    { icon: Headset, text: t("pricing.ultra.feature3") },
                    { icon: Gauge, text: t("pricing.ultra.feature4") },
                    { icon: InfinityIcon, text: t("pricing.ultra.feature5") },
                    { icon: Zap, text: t("pricing.ultra.feature6") },
                  ]}
                />

                <div className="mt-auto">
                  {tier === "ultra" ? (
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-xs text-purple-400 font-semibold uppercase tracking-wide">
                          {t("pricing.currentPlan")}
                        </p>
                        {isUltraTrial && (
                          <span className="inline-flex items-center rounded-full bg-purple-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-purple-400">
                            {t("pricing.trialActiveBadge")}
                          </span>
                        )}
                      </div>
                      {isUltraTrial && trialEndsAtLabel && (
                        <p className="text-sm text-(--text-primary) font-medium">
                          {t("pricing.trialEndsOn", { date: trialEndsAtLabel })}
                        </p>
                      )}
                      {isUltraTrial && (
                        <p className="text-sm text-(--text-primary) leading-relaxed">
                          {t("pricing.trialBillingNote", { amount: "$199" })}
                        </p>
                      )}
                      {isCancelled && endsAtLabel && (
                        <p className="text-xs text-(--text-muted)">
                          {t("pricing.ultra.cancelledNotice", { date: endsAtLabel })}
                        </p>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        fullWidth
                        onClick={() => handleManage()}
                        disabled={loading !== null}
                        loading={loading === "manage"}
                      >
                        {loading === "manage"
                          ? t("pricing.ultra.manageBtnLoading")
                          : t("pricing.ultra.manageBtn")}
                      </Button>
                      {!isCancelled && (
                        <Button
                          variant="ghost"
                          size="sm"
                          fullWidth
                          onClick={() => handleCancel()}
                          disabled={loading !== null}
                          className="text-xs text-(--text-faint) hover:text-(--status-danger) hover:bg-transparent"
                        >
                          {t("pricing.cancelBtn")}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <>
                      {isProTrial && ultraIntroEligible ? (
                        <p className="text-xs text-purple-400 mb-2 leading-relaxed">
                          {renderSubscriptionPlanNames(
                            t("pricing.ultra.trialSwitchNotice"),
                          )}
                        </p>
                      ) : ultraPreview ? (
                        <p className="text-xs text-purple-400 mb-2">
                          {t("pricing.proratedToday", {
                            amount: formatCurrency(
                              ultraPreview.amountDueNow,
                              ultraPreview.currency,
                              locale,
                            ),
                          })}
                        </p>
                      ) : null}
                      <Button
                        variant="primary"
                        size="sm"
                        fullWidth
                        onClick={() => handleUpgradeClick("ultra")}
                        disabled={loading !== null || status === "loading"}
                        loading={loading === "upgrade-ultra" || status === "loading"}
                        className="h-auto min-h-11 whitespace-normal bg-purple-600 py-2.5 leading-tight text-white wrap-break-word hover:bg-purple-700"
                      >
                        {loading === "upgrade-ultra"
                          ? t("pricing.ultra.upgradeBtnLoading")
                          : isProTrial && ultraIntroEligible
                            ? t("pricing.ultra.trialSwitchBtn")
                            : tier === "free" && ultraIntroEligible
                              ? t("pricing.ultra.upgradeBtnIntro")
                              : t("pricing.ultra.upgradeBtn")}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Link
        href="/subscription-access"
        className="group relative isolate flex flex-col overflow-hidden rounded-2xl border border-(--accent)/30 bg-linear-to-r from-(--accent)/10 via-(--bg-card) to-violet-500/10 px-5 py-5 transition duration-200 hover:-translate-y-0.5 hover:border-(--accent)/55 sm:flex-row sm:items-center sm:gap-5 sm:px-6"
      >
        <div
          className="pointer-events-none absolute -right-12 -top-16 -z-10 size-48 rounded-full bg-violet-500/10 blur-3xl"
          aria-hidden
        />
        <Image
          src="/carpy/poses/subscriptions.png"
          alt={t("pricing.planDetails.mascotAlt")}
          width={96}
          height={96}
          sizes="96px"
          className="mx-auto h-24 w-24 shrink-0 object-contain drop-shadow-[0_12px_20px_rgba(0,0,0,0.24)] sm:mx-0"
        />
        <span className="min-w-0 flex-1 text-center sm:text-left">
          <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-(--accent)">
            {t("pricing.planDetails.eyebrow")}
          </span>
          <span className="mt-1 block text-lg font-black text-(--text-primary)">
            {t("pricing.planDetails.title")}
          </span>
          <span className="mt-1 block text-sm leading-6 text-(--text-muted)">
            {renderSubscriptionPlanNames(t("pricing.planDetails.body"))}
          </span>
        </span>
        <span className="mt-4 inline-flex shrink-0 items-center justify-center gap-2 text-sm font-bold text-(--accent) sm:mt-0">
          {t("pricing.planDetails.cta")}
          <ArrowRight
            className="size-4 transition-transform group-hover:translate-x-1"
            aria-hidden
          />
        </span>
      </Link>

      {error && (
        <div className="rounded-xl border border-(--status-danger-border) bg-(--status-danger-bg) px-4 py-3 text-sm text-(--status-danger-text) flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span>{error}</span>
          {paymentDeclined && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleManage()}
              disabled={loading !== null}
              loading={loading === "manage"}
              className="self-start sm:self-auto whitespace-nowrap"
            >
              {loading === "manage"
                ? t("pricing.errors.openingPortal")
                : t("pricing.errors.updatePaymentMethod")}
            </Button>
          )}
        </div>
      )}

      <p className="text-center text-xs text-(--text-faint)">
        {t("pricing.disclaimer")}
      </p>

      <Link
        href="/api"
        className="group mx-auto mt-8 flex w-full max-w-3xl items-center gap-4 rounded-2xl border border-(--border) bg-(--bg-card) px-5 py-4 transition-colors hover:border-(--accent)/40 hover:bg-(--accent-bg)/30"
      >
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-(--accent-bg) text-(--accent)">
          <Code2 className="h-5 w-5" />
        </span>
        <span className="flex flex-1 flex-col gap-0.5">
          <span className="text-sm font-semibold text-(--text-primary)">
            {t("api.banner.title")}
          </span>
          <span className="text-xs text-(--text-secondary)">
            {t("api.banner.body")}
          </span>
        </span>
        <span className="hidden whitespace-nowrap text-sm font-medium text-(--accent) sm:inline">
          {t("api.banner.cta")}
        </span>
      </Link>

      {showConfirmUpgradeModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => {
            if (loading === null) setShowConfirmUpgradeModal(false);
          }}
        >
          <div
            className="relative w-full max-w-md rounded-2xl border border-(--border) bg-(--bg-card) p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setShowConfirmUpgradeModal(false)}
              disabled={loading !== null}
              aria-label={t("pricing.confirmUpgradeModal.dismiss")}
              className="absolute top-3 right-3 rounded-lg"
            >
              <X className="w-4 h-4" aria-hidden />
            </Button>
            <div className="flex items-center gap-3 pr-8">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-purple-500/15 text-purple-400 shrink-0">
                <Crown className="w-5 h-5" aria-hidden />
              </span>
              <h3 className="text-lg font-bold text-(--text-primary)">
                {t("pricing.confirmUpgradeModal.title")}
              </h3>
            </div>
            <div className="mt-4 space-y-3 text-sm text-(--text-muted) leading-relaxed">
              {showProrationLine && ultraPreview && (
                <p>
                  {t("pricing.confirmUpgradeModal.prorationLine", {
                    amount: formatCurrency(
                      ultraPreview.amountDueNow,
                      ultraPreview.currency,
                      locale,
                    ),
                    renewalDate: ultraPreview.nextRenewalDate
                      ? new Date(
                          ultraPreview.nextRenewalDate,
                        ).toLocaleDateString(dateLocale, {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "—",
                  })}
                </p>
              )}
              {showAddonLine && (
                <p>{t("pricing.confirmUpgradeModal.addonLine")}</p>
              )}
              <p>{t("pricing.confirmUpgradeModal.activatesNote")}</p>
            </div>
            <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowConfirmUpgradeModal(false)}
                disabled={loading !== null}
              >
                {t("pricing.confirmUpgradeModal.cancel")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleConfirmUpgrade}
                disabled={loading !== null}
                className="bg-purple-600 text-white hover:bg-purple-700"
              >
                {showProrationLine && ultraPreview
                  ? t("pricing.confirmUpgradeModal.confirmWithProration", {
                      amount: formatCurrency(
                        ultraPreview.amountDueNow,
                        ultraPreview.currency,
                        locale,
                      ),
                    })
                  : t("pricing.confirmUpgradeModal.confirmDefault")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <AppModal
        open={showCancelModal}
        onClose={() => {
          if (loading !== "cancel") setShowCancelModal(false);
        }}
        closeDisabled={loading === "cancel"}
        backdropLabel={t("common.close")}
        ariaLabelledBy="cancel-trial-title"
        ariaDescribedBy="cancel-trial-body"
      >
        <div className="relative z-10 w-full max-w-md rounded-2xl border border-(--border) bg-(--bg-secondary) p-6 shadow-2xl">
          <div className="flex items-start gap-3.5">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-(--status-danger-border) bg-(--status-danger-bg) text-(--status-danger)">
              <AlertTriangle className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <h2
                id="cancel-trial-title"
                className="text-base font-bold text-(--text-primary)"
              >
                {t("pricing.cancelModal.title")}
              </h2>
              <p
                id="cancel-trial-body"
                className="mt-1.5 text-sm leading-relaxed text-(--text-muted)"
              >
                {trialEndsAtLabel
                  ? t("pricing.cancelModal.body", { date: trialEndsAtLabel })
                  : t("pricing.cancelModal.bodyNoDate")}
              </p>
            </div>
          </div>
          <div className="mt-5 space-y-3">
            <div>
              <span className="block text-xs font-semibold text-(--text-secondary)">
                {t("pricing.cancelModal.reasonLabel")}
              </span>
              <SelectPopover
                value={cancelReason}
                onChange={setCancelReason}
                options={[
                  { value: "too_expensive", label: t("pricing.cancelModal.reasonPrice") },
                  { value: "not_enough_useful_cars", label: t("pricing.cancelModal.reasonInventory") },
                  { value: "missing_feature", label: t("pricing.cancelModal.reasonFeature") },
                  { value: "hard_to_use", label: t("pricing.cancelModal.reasonUsability") },
                  { value: "temporary_pause", label: t("pricing.cancelModal.reasonPause") },
                  { value: "other", label: t("pricing.cancelModal.reasonOther") },
                ]}
                placeholder={t("pricing.cancelModal.reasonPlaceholder")}
                ariaLabel={t("pricing.cancelModal.reasonLabel")}
                disabled={loading === "cancel"}
                searchable={false}
                containerClassName="mt-1.5"
                triggerClassName="h-11 rounded-xl bg-(--bg-card) text-sm text-(--text-primary)"
              />
            </div>
            <label className="block text-xs font-semibold text-(--text-secondary)">
              {t("pricing.cancelModal.detailsLabel")}
              <Textarea
                value={cancelDetails}
                onChange={(event) => setCancelDetails(event.target.value)}
                maxLength={500}
                rows={3}
                disabled={loading === "cancel"}
                placeholder={t("pricing.cancelModal.detailsPlaceholder")}
                className="mt-1.5 w-full resize-none bg-(--bg-card) text-sm text-(--text-primary) placeholder:text-(--text-faint)"
              />
            </label>
          </div>
          {error ? (
            <p className="mt-4 text-xs font-medium text-(--status-danger-text)">{error}</p>
          ) : null}
          <div className="mt-6 flex flex-col gap-2.5">
            <Button
              variant="primary"
              fullWidth
              onClick={() => {
                setShowCancelModal(false);
                setCancelReason("");
                setCancelDetails("");
              }}
              disabled={loading === "cancel"}
            >
              {t("pricing.cancelModal.keep")}
            </Button>
            <Button
              variant="secondary"
              fullWidth
              onClick={() => {
                setShowCancelModal(false);
                void handleManage();
              }}
              disabled={loading === "cancel"}
            >
              {t("pricing.cancelModal.options")}
            </Button>
            <Button
              variant="destructive"
              fullWidth
              onClick={() => confirmCancel()}
              disabled={loading === "cancel" || !cancelReason}
            >
              {loading === "cancel"
                ? t("pricing.cancelModal.cancelling")
                : t("pricing.cancelModal.confirm")}
            </Button>
          </div>
        </div>
      </AppModal>
    </div>
  );
}
